import type { RefObject } from 'react';
import { useEffect } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import cloneDeep from 'lodash-es/cloneDeep';
import set from 'lodash-es/set';

import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import { Box, Button } from '@rover/kibble/core';
import { Props as ButtonProps } from '@rover/kibble/core/Button';
import { getApiPersonFullSitterProfileRetrieveQueryKey } from '@rover/rsdk/src/apiClient/latest/api-person-full-sitter-profile-retrieve';
import { useApiRelationshipDetailUpdate } from '@rover/rsdk/src/apiClient/latest/api-relationship-detail-update';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import emitMetric from '@rover/utilities/emitMetric';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';
import { redirect } from '@rover/utilities/url';

import { OwnerConversionSitterProfileContactSitterEvent } from '../SitterProfilePage/analyticsEvents';

import ShortlistContactActions from './ShortlistContactActions/ShortlistContactActions';
import type { PagePosition, ShortlistLayout } from './ShortlistContactActions/types';

type Props = ButtonProps & {
  sitterInfo: EnhancedPersonSitterProfile | undefined;
  sitterSlug: string;
  contactUrl: string;
  isLoggedIn?: boolean;
  pagePosition: PagePosition;
  shouldShowRevampLayout?: boolean;
  /** Receives the contact list's main button, for callers that move focus to it. */
  primaryButtonRef?: RefObject<HTMLElement | null>;
};

const getShortlistLayout = (isHeader: boolean, isRevamp: boolean): ShortlistLayout => {
  if (isHeader) return 'header';
  return isRevamp ? 'stacked' : 'legacy';
};

const ContactButton = ({
  sitterInfo,
  sitterSlug,
  contactUrl,
  isLoggedIn,
  pagePosition,
  shouldShowRevampLayout = false,
  primaryButtonRef,
  ...other
}: Props): JSX.Element => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { isSuccess, data } = useApiRelationshipDetailUpdate({
    mutation: { retry: 3 },
  });
  const { isVisible: isShortlistVisible } = useShortlist();
  const isHeader = pagePosition === 'main' && shouldShowRevampLayout;

  useEffect(() => {
    if (isSuccess) {
      // Every stay quoted for this sitter is cached under its own key.
      queryClient.setQueriesData<EnhancedPersonSitterProfile>(
        getApiPersonFullSitterProfileRetrieveQueryKey({ slug: sitterSlug }),
        (cached) => {
          if (!cached) return cached;
          const updatedData = cloneDeep(cached);
          set(updatedData, 'authenticatedContext.isFavorite', data.isFavorite);
          return updatedData;
        }
      );
    }
  }, [isSuccess]);

  const handleOnPressContact = (): void => {
    emitMetric('pages_sitter_profile_contact_sitter_pressed', { pagePosition });
    Logger.info('pages_sitter_profile_contact_sitter_pressed');
    const pageVariantProps = shouldShowRevampLayout ? { pageVariant: 'revamp' as const } : {};
    storeCrossPageAnalyticsEvent(
      new OwnerConversionSitterProfileContactSitterEvent({ pagePosition, ...pageVariantProps })
    );
    if (!isLoggedIn) redirect('/account/continue/');
    redirect(contactUrl);
  };

  if (isShortlistVisible) {
    return (
      <ShortlistContactActions
        sitter={sitterInfo}
        pagePosition={pagePosition}
        layout={getShortlistLayout(isHeader, shouldShowRevampLayout)}
        primaryButtonRef={primaryButtonRef}
        {...other}
      />
    );
  }

  return (
    <Button
      variant="primary"
      width={isHeader ? undefined : '100%'}
      px={isHeader ? '6x' : undefined}
      maxWidth={isHeader ? '200px' : undefined}
      onClick={handleOnPressContact}
      data-testid={`contact-button-${pagePosition}`}
      {...other}
    >
      <Box
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {t`Contact`}
      </Box>
    </Button>
  );
};

export default ContactButton;
